import { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, Paper, Skeleton, Typography } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { getMerchantId } from 'redux/reducers/auth/selectors';
import { getSupportedCountryListRequest } from 'redux/reducers/settings/reducer';
import {
	getAllCountriesOptions,
	getSupportedCountryList,
	getSupportedCountryListLoader,
} from 'redux/reducers/settings/selectors';
import { SupportedCountryCard } from './SupportedCountryCard';

const ROWS = Array.from(new Array(10));

type TSupportedCountryLike =
	| string
	| {
			country_code?: string;
			name?: string;
	  };

export const SupportedCountries: FC = () => {
	const dispatch = useDispatch();
	const xMerchantId = useSelector(getMerchantId);
	const supportedCountryList = useSelector(getSupportedCountryList);
	const supportedCountryListLoader = useSelector(getSupportedCountryListLoader);
	const countries = useSelector(getAllCountriesOptions);

	useEffect(() => {
		dispatch(getSupportedCountryListRequest());
	}, [dispatch]);

	const sortedCountries = useMemo(() => {
		const list = (supportedCountryList ?? []) as TSupportedCountryLike[];

		const countryLabelByCode = new Map(countries.map((c) => [c.code, c.label]));

		const normalized = list
			.map((item) => {
				if (typeof item === 'string') {
					return { code: item, name: countryLabelByCode.get(item) ?? item };
				}
				return {
					code: item.country_code ?? '',
					name:
						item.name ?? countryLabelByCode.get(item.country_code ?? '') ?? item.country_code ?? '',
				};
			})
			.filter((x) => x.code || x.name);

		return normalized.sort((a, b) => String(a.name).localeCompare(String(b.name)));
	}, [supportedCountryList, countries]);

	return (
		<Container>
			<Paper
				sx={{
					padding: '20px',
					marginBottom: '20px',
					overflowX: 'auto',
					width: 'auto',
				}}
			>
				<Box
					sx={{
						width: 'auto',
						minWidth: '650px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}
					>
						<Typography
							component="h1"
							variant="h5"
							color="primary"
							sx={{ fontWeight: 'bold', marginBottom: 3 }}
						>
							Supported Countries
						</Typography>

						{xMerchantId && (
							<Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 3, color: 'gray' }}>
								Merchant #{xMerchantId}
							</Typography>
						)}
					</Box>

					{!supportedCountryListLoader && supportedCountryList && (
						<Box>
							{sortedCountries.length ? (
								<Box
									sx={{
										display: 'grid',
										gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
										gap: 2,
									}}
								>
									{sortedCountries.map((country) => (
										<SupportedCountryCard
											key={country.code || country.name}
											code={country.code}
											name={country.name}
										/>
									))}
								</Box>
							) : (
								<Typography color="text.secondary">No supported countries.</Typography>
							)}
						</Box>
					)}

					{!supportedCountryListLoader && !supportedCountryList && (
						<Typography color="text.secondary">No data.</Typography>
					)}

					{supportedCountryListLoader && (
						<Box sx={{ pb: '1px' }}>
							{ROWS.map(() => (
								<Skeleton
									key={nanoid()}
									variant="rectangular"
									height="52px"
									sx={{
										mb: '12px',
									}}
								/>
							))}
						</Box>
					)}
				</Box>
			</Paper>
		</Container>
	);
};
